import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { CreateDatabaseResponse, Database, UsageResponse } from '@tallypg/contracts';
import { api, formatBytes, formatDate, formatMoney, useApi, type Forecast } from '../api';
import { ConnectModal, CredentialsReveal, Modal, StatusBadge } from '../components';
import { UsageChart } from './Usage';
import { LocationCard } from './DatabaseLocation';

export function DatabaseDetailPage() {
  const { databaseId } = useParams();
  const nav = useNavigate();
  const data = useApi<{ database: Database }>(`/databases/${databaseId}`);
  const usage = useApi<UsageResponse>(`/databases/${databaseId}/usage`);
  const forecast = useApi<Forecast>('/forecast');
  const [connect, setConnect] = useState(false);
  const [rotated, setRotated] = useState<CreateDatabaseResponse | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'anywhere' | 'allowlist' | null>(null);
  const [cidrText, setCidrText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (data.error) return <p className="error">{data.error.message}</p>;
  if (!data.data) return <p className="muted">Loading…</p>;
  const d = data.data.database;
  const currentMode = mode ?? (d.networkRules.length ? 'allowlist' : 'anywhere');
  const currentCidrs = cidrText ?? d.networkRules.join('\n');

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="muted">
        <Link to="/databases">Databases</Link> / {d.name}
      </p>
      <div className="row between">
        <h1>
          {d.name} <StatusBadge state={d.state} />
        </h1>
        <div className="row">
          <Link to={`/databases/${d.id}/data`} className="btn secondary">
            Browse data
          </Link>
          <button onClick={() => setConnect(true)} disabled={d.state !== 'active'}>
            Connect
          </button>
          <button
            className="secondary"
            disabled={busy || d.state !== 'active'}
            onClick={() =>
              run(async () =>
                setRotated(
                  await api<CreateDatabaseResponse>(`/databases/${d.id}/rotate-password`, { method: 'POST' }),
                ),
              )
            }
          >
            Rotate password
          </button>
        </div>
      </div>
      {d.state === 'payment_required' && (
        <div className="banner danger">
          Connections are paused until you <Link to="/billing">add credit</Link>. Your data is kept.
        </div>
      )}
      {d.state === 'failed' && (
        <div className="banner danger">
          Creation failed{d.failureReason ? `: ${d.failureReason}` : ''}.{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              void run(async () => {
                setRotated(await api<CreateDatabaseResponse>(`/databases/${d.id}/retry`, { method: 'POST' }));
                await data.reload();
              });
            }}
          >
            Try again
          </a>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <div className="grid cols-3">
        <div className="card">
          <div className="stat">Size</div>
          <div className="stat-value">{formatBytes(d.sizeBytes)}</div>
        </div>
        <div className="card">
          <div className="stat">Cost this month</div>
          <div className="stat-value">{formatMoney(d.monthToDateMicroUsd)}</div>
          {(() => {
            const p = forecast.data?.databases[d.id];
            if (!p || p.sampleDays === 0)
              return <div className="muted">projection appears after a day of use</div>;
            return (
              <div className="muted">
                on track for {formatMoney(p.projectedMonthMicro)} this month · {formatMoney(p.dailyRateMicro)}
                /day over the last {p.sampleDays} day{p.sampleDays === 1 ? '' : 's'}
              </div>
            );
          })()}
        </div>
        <div className="card">
          <div className="stat">Last activity</div>
          <div className="stat-value" style={{ fontSize: 16 }}>
            {formatDate(d.lastActivityAt)}
          </div>
        </div>
      </div>
      <LocationCard database={d} onChanged={() => data.reload()} />
      <div className="card">
        <h2>Recent activity (last 30 days)</h2>
        {usage.data && <UsageChart usage={usage.data} />}
      </div>
      <div className="card">
        <h2>Connection</h2>
        <table>
          <tbody>
            <tr>
              <th>Host</th>
              <td>
                <code>{d.connection.host}</code>
              </td>
            </tr>
            <tr>
              <th>Ports</th>
              <td>
                <code>{d.connection.port}</code> sessions · <code>{d.connection.poolerPort}</code> transaction
                pooling
              </td>
            </tr>
            <tr>
              <th>User</th>
              <td>
                <code>{d.connection.user}</code>
              </td>
            </tr>
            <tr>
              <th>Database</th>
              <td>
                <code>{d.connection.database}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>Network security</h2>
        <p className="muted">
          By default connections are accepted from anywhere, as long as the password and TLS check out. You
          can restrict them to specific IPv4/IPv6 ranges.
        </p>
        <div className="field">
          <label>
            <input
              type="radio"
              checked={currentMode === 'anywhere'}
              onChange={() => setMode('anywhere')}
              style={{ width: 'auto', marginRight: 6 }}
            />
            Allow from anywhere
          </label>
          <label>
            <input
              type="radio"
              checked={currentMode === 'allowlist'}
              onChange={() => setMode('allowlist')}
              style={{ width: 'auto', marginRight: 6 }}
            />
            Allow only these address ranges
          </label>
        </div>
        {currentMode === 'allowlist' && (
          <div className="field">
            <textarea
              rows={4}
              placeholder={'203.0.113.10/32\n2001:db8::/32'}
              value={currentCidrs}
              onChange={(e) => setCidrText(e.target.value)}
            />
          </div>
        )}
        <button
          disabled={busy || !['active', 'payment_required'].includes(d.state)}
          onClick={() =>
            run(async () => {
              await api(`/databases/${d.id}/network-rules`, {
                method: 'PUT',
                json: {
                  mode: currentMode,
                  cidrs:
                    currentMode === 'allowlist'
                      ? currentCidrs
                          .split(/\s+|,/)
                          .map((s) => s.trim())
                          .filter(Boolean)
                      : [],
                },
              });
              setMode(null);
              setCidrText(null);
              await data.reload();
            })
          }
        >
          Save network rules
        </button>
      </div>
      <div className="card">
        <h2>Delete database</h2>
        <p className="muted">
          This permanently deletes the database and its data. Type the database name to confirm.
        </p>
        <div className="row">
          <input
            placeholder={d.name}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <button
            className="danger"
            disabled={deleting || confirmName !== d.name}
            onClick={() =>
              run(async () => {
                setDeleting(true);
                await api(`/databases/${d.id}`, { method: 'DELETE', json: { confirmName } });
                nav('/databases');
              })
            }
          >
            Delete permanently
          </button>
        </div>
      </div>
      {connect && <ConnectModal database={d} onClose={() => setConnect(false)} />}
      {rotated && (
        <Modal title="New credentials" onClose={() => setRotated(null)}>
          <p className="muted">The previous password no longer works. Existing connections were closed.</p>
          <CredentialsReveal result={rotated} />
        </Modal>
      )}
    </div>
  );
}
